<template>
  <div class="platform-page">
    <Navbar :currentLang="currentLang" @change-lang="changeLang" />

    <!-- Hero -->
    <section class="platform-hero">
      <div class="platform-hero-inner">
        <div class="platform-hero-text" data-aos="fade-up">
          <span class="section-eyebrow">Platform</span>
          <h1 class="platform-hero-title">One Platform to Power<br>Your Entire Supply Chain</h1>
          <p class="platform-hero-sub">
            From sourcing to delivery, manage every step in one dashboard. Real-time inventory, automated workflows, and full visibility — built for scaling brands.
          </p>
          <div class="platform-hero-btns">
            <button class="btn-primary" @click="scrollToSection('features')">Explore Features</button>
            <button class="btn-ghost" @click="scrollToSection('demo')">
              <svg viewBox="0 0 24 24" fill="currentColor" style="width:18px;height:18px"><path d="M8 5v14l11-7z"/></svg>
              Watch Demo
            </button>
          </div>
        </div>
        <div class="platform-hero-visual" data-aos="fade-left">
          <div class="hero-img-wrap">
            <img src="https://static.wixstatic.com/media/575a2a_3f47341d16ca4f4b9e2f8ed488e08070~mv2.jpg/v1/fit/w_800,h_500,q_90,enc_avif,quality_auto/575a2a_3f47341d16ca4f4b9e2f8ed488e08070~mv2.jpg" alt="Platform Dashboard" />
            <div class="hero-img-badge">
                <span class="img-badge-dot"></span>
                Live Dashboard
              </div>
          </div>
          <div class="hero-float-card hero-float--1">
            <div class="float-icon--blue"><i class="fa-solid fa-chart-line"></i></div>
            <div>
              <div class="float-num">98.5%</div>
              <div class="float-label">On-Time Rate</div>
            </div>
          </div>
          <div class="hero-float-card hero-float--2">
            <div class="float-icon--green"><i class="fa-solid fa-boxes-stacked"></i></div>
            <div>
              <div class="float-num">1,247</div>
              <div class="float-label">Orders Today</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Core Features -->
    <section class="features-section" id="features">
      <div class="features-inner">
        <div class="section-header" data-aos="fade-up">
          <span class="section-eyebrow">Core Features</span>
          <h2 class="section-title">Everything You Need to Scale</h2>
          <p class="section-desc">Powerful tools designed for modern e-commerce brands. No bloat, no complexity — just what works.</p>
        </div>
        <div class="features-grid">
          <div class="feature-card" v-for="(f, i) in features" :key="i" data-aos="fade-up" :data-aos-delay="i * 80">
            <div class="feature-icon" :style="{ background: f.bg }">
              <i :class="f.icon"></i>
            </div>
            <h3 class="feature-title">{{ f.title }}</h3>
            <p class="feature-desc">{{ f.desc }}</p>
            <div class="feature-tags">
              <span class="feature-tag" v-for="(tag, j) in f.tags" :key="j">{{ tag }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Dashboard Showcase -->
    <section class="showcase-section" id="demo">
      <div class="showcase-inner">
        <div class="showcase-text" data-aos="fade-right">
          <span class="section-eyebrow">Real-Time Dashboard</span>
          <h2 class="section-title">See Your Business at a Glance</h2>
          <p class="section-desc">Track orders, inventory, and shipments in real time. Get alerts when action is needed. Make data-driven decisions with built-in analytics.</p>
          <ul class="showcase-list">
            <li><i class="fa-solid fa-check-circle"></i> Live order tracking across all channels</li>
            <li><i class="fa-solid fa-check-circle"></i> Inventory alerts & automatic reordering</li>
            <li><i class="fa-solid fa-check-circle"></i> Shipping performance metrics</li>
            <li><i class="fa-solid fa-check-circle"></i> Custom reports & data export</li>
          </ul>
        </div>
        <div class="showcase-visual" data-aos="fade-left">
          <div class="showcase-img-wrapper">
            <!-- Second dashboard mockup -->
            <div class="dashboard-mockup dashboard-mockup--dark">
              <div class="mockup-body" style="padding:20px;">
                <div class="mockup-row" style="display:flex;gap:12px;margin-bottom:16px;">
                  <div class="mini-stat" v-for="s in miniStats" :key="s.label">
                    <div class="mini-stat-label">{{ s.label }}</div>
                    <div class="mini-stat-value">{{ s.value }}</div>
                    <div class="mini-stat-trend" :class="s.up ? 'up' : 'down'">{{ s.trend }}</div>
                  </div>
                </div>
                <div style="background:#1e293b;border-radius:10px;padding:16px;">
                  <div style="color:#94a3b8;font-size:0.78rem;margin-bottom:10px;">ORDER STATUS BREAKDOWN</div>
                  <div v-for="(os, idx) in orderStatus" :key="idx" style="display:flex;align-items:center;gap:10px;margin-bottom:8px;">
                    <div :style="{background:os.color,width:'8px',height:'8px',borderRadius:'2px'}"></div>
                    <span style="color:#cbd5e1;font-size:0.8rem;flex:1;">{{ os.label }}</span>
                    <span style="color:#fff;font-size:0.82rem;font-weight:600;">{{ os.pct }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Integrations -->
    <section class="integrations-section">
      <div class="integrations-inner">
        <div class="section-header" data-aos="fade-up">
          <span class="section-eyebrow">Integrations</span>
          <h2 class="section-title">Connect Your Entire Tech Stack</h2>
          <p class="section-desc">Seamlessly integrate with the platforms you already use. One-click setup, no developer needed.</p>
        </div>
        <div class="integrations-grid" data-aos="fade-up" data-aos-delay="100">
          <div class="integration-card" v-for="(int, i) in integrations" :key="i">
            <div class="integration-logo" :style="{ background: int.bg }">
              <i :class="int.icon"></i>
            </div>
            <h4 class="integration-name">{{ int.name }}</h4>
            <p class="integration-desc">{{ int.desc }}</p>
            <span class="integration-badge" v-if="int.featured">Recommended</span>
          </div>
        </div>
        <div class="integrations-cta" data-aos="fade-up">
          <p>Don't see your platform?</p>
          <button class="btn-outline">View All Integrations (50+)</button>
        </div>
      </div>
    </section>

    <!-- API / Technology -->
    <section class="tech-section">
      <div class="tech-inner">
        <div class="section-header" data-aos="fade-up">
          <span class="section-eyebrow">For Developers</span>
          <h2 class="section-title">Powerful API & Webhooks</h2>
          <p class="section-desc">Build custom workflows with our REST API. Real-time event notifications via webhooks. Full documentation and SDKs included.</p>
        </div>
        <div class="tech-showcase" data-aos="fade-up" data-aos-delay="100">
          <div class="code-block">
            <div class="code-header">
              <span>REST API Example</span>
              <div class="code-dots"><span></span><span></span><span></span></div>
            </div>
            <pre class="code-content"><code>curl -X POST https://api.dropone.com/v1/orders \
  -H "Authorization: Bearer YOUR_API_KEY" \
  -H "Content-Type: application/json" \
  -d '{
    "order_id": "ORD-001",
    "customer": { "name": "John Doe", "email": "..." },
    "items": [{ "sku": "BLUE-MUG-01", "qty": 2 }],
    "shipping_address": { ... }
  }'

  # Response
  {
    "success": true,
    "order_id": "ORD-001",
    "tracking_no": "DHL1234567890",
    "estimated_delivery": "2024-03-15"
  }</code></pre>
          </div>
          <div class="tech-features">
            <div class="tech-feature" v-for="(tf, i) in techFeatures" :key="i">
              <div class="tech-feature-icon"><i :class="tf.icon"></i></div>
              <div>
                <h4>{{ tf.title }}</h4>
                <p>{{ tf.desc }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Security -->
    <section class="security-section">
      <div class="security-inner" data-aos="fade-up">
        <div class="security-badges">
          <div class="security-badge" v-for="(sb, i) in securityBadges" :key="i">
            <div class="security-badge-icon">{{ sb.emoji }}</div>
            <h4>{{ sb.title }}</h4>
            <p>{{ sb.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="platform-cta">
      <div class="platform-cta-inner" data-aos="fade-up">
        <h2>Ready to streamline your supply chain?</h2>
        <p>Join 10,000+ sellers who trust FULFILLDROP to power their fulfillment.</p>
        <div class="platform-cta-btns">
          <button class="btn-primary" @click="scrollToContact">Get Started Free</button>
          <button class="btn-ghost">Schedule a Demo</button>
        </div>
      </div>
    </section>

    <Footer :currentLang="currentLang" />
  </div>
</template>

<script>
import Navbar from '../components/Navbar.vue'
import Footer from '../components/Footer.vue'

export default {
  name: 'Platform',
  components: { Navbar, Footer },
  data() {
    return {
      currentLang: 'en',
      features: [
        { title: 'Order Management', desc: 'Sync orders from all sales channels. Auto-route to optimal warehouse. Track every status change.', icon: 'fa-solid fa-cart-shopping', bg: '#e6f7f9', tags: ['Multi-channel', 'Auto-routing', 'Tracking'] },
        { title: 'Inventory Tracking', desc: 'Real-time stock levels across all warehouses. Low-stock alerts. Automated reorder points.', icon: 'fa-solid fa-boxes-stacked', bg: '#f0fdf4', tags: ['Real-time', 'Alerts', 'Reordering'] },
        { title: 'Quality Control', desc: 'Multi-stage inspection process. Photo proof of QC. Reject defective items before shipping.', icon: 'fa-solid fa-magnifying-glass', bg: '#fef3c7', tags: ['Inspection', 'Photo Proof', 'Rejection'] },
        { title: 'Analytics & Reports', desc: 'Data-driven insights into your supply chain. Custom reports. Export to CSV/Excel.', icon: 'fa-solid fa-chart-line', bg: '#fce7f3', tags: ['Insights', 'Custom Reports', 'Export'] },
        { title: 'Branded Packaging', desc: 'Custom boxes, inserts, thank-you cards. White-label shipping. Elevate unboxing experience.', icon: 'fa-solid fa-gift', bg: '#dbeafe', tags: ['Custom Boxes', 'Inserts', 'White-label'] },
        { title: 'Returns Management', desc: 'Streamlined reverse logistics. Inspect returned items. Restock or dispose with one click.', icon: 'fa-solid fa-rotate-left', bg: '#f0fdf4', tags: ['Reverse Logistics', 'Inspection', 'Restock'] },
      ],
      miniStats: [
        { label: 'Orders Today', value: '1,247', trend: '+12%', up: true },
        { label: 'Avg Delivery', value: '3.2d', trend: '-8%', up: true },
        { label: 'Return Rate', value: '2.1%', trend: '-0.3%', up: true },
      ],
      orderStatus: [
        { label: 'Delivered', pct: '68%', color: '#10b981' },
        { label: 'In Transit', pct: '22%', color: '#3b82f6' },
        { label: 'Processing', pct: '8%', color: '#f59e0b' },
        { label: 'Exception', pct: '2%', color: '#ef4444' },
      ],
      integrations: [
        { name: 'Shopify', desc: 'Sync products, orders, and inventory automatically.', icon: 'fa-brands fa-shopify', bg: '#e6f7f9', featured: true },
        { name: 'WooCommerce', desc: 'WordPress-powered stores. Full feature parity.', icon: 'fa-brands fa-wordpress', bg: '#f0fdf4', featured: false },
        { name: 'Amazon FBA', desc: 'FBA prep, labeling, and direct shipping.', icon: 'fa-brands fa-amazon', bg: '#fef3c7', featured: true },
        { name: 'TikTok Shop', desc: 'Social commerce fulfillment made easy.', icon: 'fa-brands fa-tiktok', bg: '#fce7f3', featured: false },
        { name: 'eBay', desc: 'Sync eBay orders and tracking numbers.', icon: 'fa-brands fa-ebay', bg: '#dbeafe', featured: false },
        { name: 'Etsy', desc: 'Handmade and custom product fulfillment.', icon: 'fa-brands fa-etsy', bg: '#f0fdf4', featured: false },
      ],
      techFeatures: [
        { title: 'REST API', desc: 'Full-featured REST API with comprehensive endpoints for orders, inventory, and tracking.', icon: 'fa-solid fa-code' },
        { title: 'Webhooks', desc: 'Real-time event notifications for order status changes, inventory updates, and more.', icon: 'fa-solid fa-bolt' },
        { title: 'SDKs', desc: 'Official libraries for Node.js, Python, PHP, and Ruby. More coming soon.', icon: 'fa-solid fa-box-open' },
        { title: 'Sandbox', desc: 'Test environment with mock data. No real orders or charges.', icon: 'fa-solid fa-flask' },
      ],
      securityBadges: [
        { emoji: '🔒', title: 'SOC 2 Type II', desc: 'Independently audited security controls.' },
        { emoji: '🛡️', title: 'GDPR Compliant', desc: 'Full compliance with EU data regulations.' },
        { emoji: '🔐', title: '256-bit SSL', desc: 'Bank-grade encryption for all data transmission.' },
        { emoji: '✅', title: '99.9% Uptime', desc: 'Reliable infrastructure with redundant systems.' },
      ]
    }
  },
  methods: {
    changeLang(lang) {
      this.currentLang = lang
    },
    scrollToSection(id) {
      const el = document.getElementById(id)
      if (el) el.scrollIntoView({ behavior: 'smooth' })
    },
    scrollToContact() {
      this.$router.push('/contact')
    }
  }
}
</script>

<style scoped>
.platform-page {
  font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
}

/* Hero */
.platform-hero {
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 40%, #312e81 100%);
  padding: 100px 2rem 60px;
  color: #fff;
  overflow: hidden;
}
.platform-hero-inner {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1.1fr;
  gap: 48px;
  align-items: center;
}
.platform-hero-text {
  padding-right: 20px;
}
.section-eyebrow {
  display: inline-block;
  background: rgba(40,181,193,0.25);
  color: #a5b4fc;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding: 5px 14px;
  border-radius: 20px;
  margin-bottom: 16px;
}
.platform-hero-title {
  font-size: 2.8rem;
  font-weight: 800;
  line-height: 1.15;
  margin: 0 0 18px;
}
.platform-hero-sub {
  font-size: 1.05rem;
  opacity: 0.82;
  line-height: 1.65;
  margin-bottom: 28px;
}
.platform-hero-btns {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
}
.btn-primary {
  background: linear-gradient(135deg, #28b5c1, #1e9aa5);
  color: #fff;
  padding: 13px 28px;
  border: none;
  border-radius: 12px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
  box-shadow: 0 4px 16px rgba(40,181,193,0.35);
}
.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 28px rgba(40,181,193,0.45);
}
.btn-ghost {
  background: rgba(255,255,255,0.1);
  color: #fff;
  padding: 13px 28px;
  border: 1.5px solid rgba(255,255,255,0.25);
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.2s, border-color 0.2s;
}
.btn-ghost:hover {
  background: rgba(255,255,255,0.18);
  border-color: rgba(255,255,255,0.4);
}
.btn-outline {
  background: #fff;
  color: #28b5c1;
  padding: 11px 24px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
}
.btn-outline:hover {
  border-color: #28b5c1;
  background: #f8f7ff;
}


/* Hero Visual – real image */
.platform-hero-visual {
  position: relative;
  min-height: 380px;
  display: flex;
  align-items: center;
}
.hero-img-wrap {
  position: relative;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0,0,0,0.18);
  width: 100%;
}
.hero-img-wrap img {
  width: 100%;
  height: auto;
  display: block;
}
.hero-img-badge {
  position: absolute;
  top: 16px;
  left: 16px;
  background: rgba(16,185,129,0.92);
  backdrop-filter: blur(6px);
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 5px 13px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  gap: 6px;
  z-index: 2;
}
.img-badge-dot {
  width: 7px;
  height: 7px;
  background: #fff;
  border-radius: 50%;
  display: inline-block;
  animation: pulse-dot 1.5s infinite;
}
@keyframes pulse-dot {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
.hero-float-card {
  position: absolute;
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(255,255,255,0.95);
  backdrop-filter: blur(8px);
  border-radius: 12px;
  padding: 12px 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.10);
  z-index: 3;
}
.hero-float--1 {
  bottom: 24px;
  left: -18px;
}
.hero-float--2 {
  bottom: 24px;
  right: -18px;
}
.float-icon--blue {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #e6f7f9;
  color: #28b5c1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  flex-shrink: 0;
}
.float-icon--green {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #ecfdf5;
  color: #10b981;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  flex-shrink: 0;
}
.float-num {
  font-size: 1.1rem;
  font-weight: 800;
  color: #1e293b;
  line-height: 1.2;
}
.float-label {
  font-size: 0.68rem;
  color: #94a3b8;
  font-weight: 500;
}

/* Showcase visual – real image */
.showcase-img-wrapper {
  position: relative;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0,0,0,0.14);
}
.showcase-img-wrapper img {
  width: 100%;
  height: auto;
  display: block;
}

/* Dashboard Mockup (legacy) */
/* .platform-hero-visual old rule removed */
.dashboard-mockup {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.25);
  overflow: hidden;
  transform: rotateY(-4deg) rotateX(2deg);
  transition: transform 0.4s;
}
.dashboard-mockup:hover {
  transform: rotateY(-1deg) rotateX(0.5deg);
}
.mockup-header {
  background: #f8fafc;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid #e2e8f0;
}
.mockup-dots {
  display: flex;
  gap: 6px;
}
.mockup-dots span {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #e2e8f0;
}
.mockup-dots span:nth-child(1) { background: #ef4444; }
.mockup-dots span:nth-child(2) { background: #f59e0b; }
.mockup-dots span:nth-child(3) { background: #10b981; }
.mockup-tabs {
  display: flex;
  gap: 16px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #94a3b8;
}
.mockup-tabs .active {
  color: #28b5c1;
}
.mockup-body {
  padding: 20px;
}
.mockup-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}
.mockup-stat {
  background: #f8fafc;
  border-radius: 10px;
  padding: 12px;
  text-align: center;
}
.mockup-stat-num {
  display: block;
  font-size: 1.3rem;
  font-weight: 800;
  color: #1e293b;
}
.mockup-stat-label {
  font-size: 0.7rem;
  color: #94a3b8;
  font-weight: 500;
}
.mockup-chart {
  margin-bottom: 16px;
  background: #f8fafc;
  border-radius: 10px;
  padding: 12px;
}
.chart-svg {
  width: 100%;
  height: auto;
}
.mockup-orders {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.mockup-order {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: #f8fafc;
  border-radius: 8px;
  font-size: 0.8rem;
}
.mockup-order-icon {
  font-size: 1.1rem;
}
.mockup-order-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.mockup-order-id {
  font-weight: 600;
  color: #334155;
  font-size: 0.78rem;
}
.mockup-order-status {
  font-size: 0.7rem;
  font-weight: 600;
}
.mockup-order-status.shipped { color: #10b981; }
.mockup-order-time {
  color: #94a3b8;
  font-size: 0.72rem;
}

/* Features */
.features-section {
  padding: 80px 2rem;
  background: #f8fafc;
}
.features-inner {
  max-width: 1100px;
  margin: 0 auto;
}
.section-header {
  text-align: center;
  margin-bottom: 48px;
}
.section-title {
  font-size: 2rem;
  font-weight: 800;
  color: #1e293b;
  margin: 12px 0 14px;
}
.section-desc {
  font-size: 1rem;
  color: #64748b;
  max-width: 560px;
  margin: 0 auto;
  line-height: 1.6;
}
.features-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}
.feature-card {
  background: #fff;
  border-radius: 16px;
  padding: 28px 24px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
  border: 1px solid #f1f5f9;
  transition: transform 0.2s, box-shadow 0.2s;
}
.feature-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 36px rgba(40,181,193,0.1);
}
.feature-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: #28b5c1;
  margin-bottom: 16px;
}
.feature-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px;
}
.feature-desc {
  font-size: 0.85rem;
  color: #64748b;
  line-height: 1.55;
  margin: 0 0 14px;
}
.feature-tags {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.feature-tag {
  background: #f1f5f9;
  color: #475569;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 6px;
}

/* Showcase */
.showcase-section {
  padding: 80px 2rem;
  background: #fff;
}
.showcase-inner {
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1.1fr;
  gap: 48px;
  align-items: center;
}
.showcase-list {
  list-style: none;
  padding: 0;
  margin: 24px 0 0;
}
.showcase-list li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  font-size: 0.92rem;
  color: #334155;
}
.showcase-list li i {
  color: #10b981;
  font-size: 1rem;
}
.showcase-img-wrapper {
  position: relative;
}
.dashboard-mockup--dark {
  background: #1e293b;
  border: 1px solid #334155;
}
.mini-stat {
  flex: 1;
  background: #1e293b;
  border-radius: 10px;
  padding: 14px;
  border: 1px solid #334155;
}
.mini-stat-label {
  font-size: 0.7rem;
  color: #94a3b8;
  margin-bottom: 4px;
}
.mini-stat-value {
  font-size: 1.3rem;
  font-weight: 800;
  color: #fff;
}
.mini-stat-trend {
  font-size: 0.72rem;
  font-weight: 600;
  margin-top: 2px;
}
.mini-stat-trend.up { color: #10b981; }
.mini-stat-trend.down { color: #ef4444; }

/* Integrations */
.integrations-section {
  padding: 80px 2rem;
  background: #f8fafc;
}
.integrations-inner {
  max-width: 1100px;
  margin: 0 auto;
  text-align: center;
}
.integrations-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 32px;
}
.integration-card {
  background: #fff;
  border-radius: 14px;
  padding: 24px 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid #f1f5f9;
  text-align: left;
  position: relative;
  transition: box-shadow 0.2s, transform 0.2s;
}
.integration-card:hover {
  box-shadow: 0 8px 24px rgba(0,0,0,0.08);
  transform: translateY(-4px);
}
.integration-logo {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  color: #28b5c1;
  margin-bottom: 14px;
}
.integration-name {
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 6px;
}
.integration-desc {
  font-size: 0.82rem;
  color: #64748b;
  line-height: 1.5;
  margin: 0;
}
.integration-badge {
  position: absolute;
  top: 14px;
  right: 14px;
  background: #e6f7f9;
  color: #28b5c1;
  font-size: 0.68rem;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 8px;
}
.integrations-cta {
  margin-top: 12px;
}
.integrations-cta p {
  color: #64748b;
  margin-bottom: 14px;
  font-size: 0.92rem;
}

/* Tech */
.tech-section {
  padding: 80px 2rem;
  background: #0f172a;
  color: #fff;
}
.tech-inner {
  max-width: 1100px;
  margin: 0 auto;
  text-align: center;
}
.tech-section .section-eyebrow {
  background: rgba(40,181,193,0.3);
  color: #a5b4fc;
}
.tech-section .section-title { color: #fff; }
.tech-section .section-desc { color: #94a3b8; }
.tech-showcase {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  text-align: left;
  margin-top: 36px;
}
.code-block {
  background: #1e293b;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #334155;
}
.code-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background: #0f172a;
  font-size: 0.78rem;
  color: #94a3b8;
  border-bottom: 1px solid #334155;
}
.code-dots {
  display: flex;
  gap: 5px;
}
.code-dots span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #334155;
}
.code-content {
  padding: 16px;
  font-size: 0.78rem;
  line-height: 1.6;
  color: #a5f3fc;
  overflow-x: auto;
  margin: 0;
}
.tech-features {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.tech-feature {
  display: flex;
  gap: 14px;
  background: rgba(255,255,255,0.05);
  border-radius: 12px;
  padding: 16px;
  border: 1px solid rgba(255,255,255,0.08);
}
.tech-feature-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(40,181,193,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a5b4fc;
  font-size: 1rem;
  flex-shrink: 0;
}
.tech-feature h4 {
  font-size: 0.92rem;
  font-weight: 700;
  color: #fff;
  margin: 0 0 4px;
}
.tech-feature p {
  font-size: 0.82rem;
  color: #94a3b8;
  margin: 0;
  line-height: 1.5;
}

/* Security */
.security-section {
  padding: 60px 2rem;
  background: #fff;
}
.security-inner {
  max-width: 900px;
  margin: 0 auto;
}
.security-badges {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
}
.security-badge {
  text-align: center;
  padding: 24px 16px;
  border-radius: 14px;
  background: #f8fafc;
  border: 1px solid #f1f5f9;
}
.security-badge-icon {
  font-size: 2rem;
  margin-bottom: 10px;
}
.security-badge h4 {
  font-size: 0.88rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 6px;
}
.security-badge p {
  font-size: 0.78rem;
  color: #64748b;
  margin: 0;
  line-height: 1.45;
}

/* CTA */
.platform-cta {
  padding: 80px 2rem;
  background: linear-gradient(135deg, #28b5c1 0%, #1e9aa5 100%);
  text-align: center;
  color: #fff;
}
.platform-cta-inner h2 {
  font-size: 2rem;
  font-weight: 800;
  margin: 0 0 10px;
}
.platform-cta-inner p {
  font-size: 1rem;
  opacity: 0.85;
  margin: 0 0 28px;
}
.platform-cta-btns {
  display: flex;
  gap: 14px;
  justify-content: center;
  flex-wrap: wrap;
}
.platform-cta .btn-primary {
  background: #fff;
  color: #28b5c1;
  box-shadow: 0 4px 16px rgba(255,255,255,0.25);
}
.platform-cta .btn-ghost {
  border-color: rgba(255,255,255,0.4);
}

/* Responsive */
@media (max-width: 1023px) {
  .platform-hero-inner {
    grid-template-columns: 1fr;
    text-align: center;
  }
  .platform-hero-btns { justify-content: center; }
  .features-grid { grid-template-columns: repeat(2, 1fr); }
  .showcase-inner { grid-template-columns: 1fr; }
  .tech-showcase { grid-template-columns: 1fr; }
  .security-badges { grid-template-columns: repeat(2, 1fr); }
  .integrations-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 640px) {
  .platform-hero-title { font-size: 2rem; }
  .features-grid { grid-template-columns: 1fr; }
  .integrations-grid { grid-template-columns: 1fr; }
  .security-badges { grid-template-columns: 1fr; }
}
</style>
